import {
  Box,
  chakra,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Flex,
} from "@chakra-ui/react";

import { useNavigate } from "react-router-dom";
import { NavList } from "./navList";
import { BrandMark } from "./ui";
import { CustomerDetails } from "./appWrapper";


interface SidebarProps {
  isOpen: boolean;
  variant?: string;
  onClose: () => void;
  CloseAfterSelect: () => void;
  userData: CustomerDetails | null;
  isPending: boolean;
}

interface SidebarBodyProps {
  CloseAfterSelect: () => void;
  userData: CustomerDetails | null;
  isPending: boolean;
  /** Called after the logo navigates home — the Drawer passes its onClose so it does not stay open over the new page. */
  onNavigate?: () => void;
}

/** Masthead → primary nav → user card. Shared by the fixed sidebar (lg+) and the mobile Drawer. */
const SidebarBody: React.FC<SidebarBodyProps> = ({ CloseAfterSelect, userData, isPending, onNavigate }) => {
  const navigate = useNavigate();

  return (
    <Flex direction="column" flex={1} minH={0} position="relative" zIndex={1} overflowY="auto">
      <Box px={6} pt={6} pb={5} borderBottom="1px solid" borderColor="border.onDark">
        <chakra.button
          type="button"
          aria-label="myBidfood home"
          display="block"
          borderRadius="sm"
          cursor="pointer"
          _focusVisible={{ boxShadow: "outlineOnDark", outline: "none" }}
          onClick={() => {
            navigate('/');
            onNavigate?.();
          }}
        >
          <BrandMark variant="dark" h="2.25rem" />
        </chakra.button>
        <Box
          as="span"
          display="inline-block"
          mt={4}
          textStyle="label"
          color="navy.200"
          bg="rgba(255,255,255,0.06)"
          borderRadius="full"
          px={3}
          py={1}
        >
          PARTNER PORTAL
        </Box>
      </Box>

      <NavList direction="column" CloseAfterSelect={CloseAfterSelect} userData={userData} isPending={isPending} />
    </Flex>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  variant,
  onClose,
  CloseAfterSelect,
  userData,
  isPending,
}) => {
  return variant === "sidebar" ? (
    <Flex
      as="aside"
      direction="column"
      w="17rem"
      flexShrink={0}
      h="100vh"
      position="sticky"
      top={0}
      layerStyle="sidebar"
      borderRight="1px solid rgba(255,255,255,0.06)"
      overflow="hidden"
    >
      {/* Static leaf-green glow anchored bottom-left; purely decorative. */}
      <Box
        aria-hidden
        position="absolute"
        left={0}
        bottom={0}
        w="320px"
        h="320px"
        pointerEvents="none"
        bg="radial-gradient(circle at 20% 100%, rgba(107,191,89,0.18), transparent 60%)"
      />
      <SidebarBody CloseAfterSelect={CloseAfterSelect} userData={userData} isPending={isPending} />
    </Flex>
  ) : (
    <Drawer isOpen={isOpen} placement="left" size="xs" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent overflow="hidden" aria-label="Navigation">
        <DrawerCloseButton aria-label="Close navigation" />
        <Box
          aria-hidden
          position="absolute"
          left={0}
          bottom={0}
          w="320px"
          h="320px"
          pointerEvents="none"
          bg="radial-gradient(circle at 20% 100%, rgba(107,191,89,0.18), transparent 60%)"
        />
        <DrawerBody h="full">
          <SidebarBody CloseAfterSelect={CloseAfterSelect} userData={userData} isPending={isPending} onNavigate={onClose} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
export default Sidebar;
